import { computed, ref, watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import {
  buildFocusedPlan,
  planLeaves,
  selectFocusedSteps,
} from '@shared/assessment-definition/dist/src/focused-plan'
import type {
  FocusedPlan,
  PlanDefinition,
  PlanPanel,
} from '@shared/assessment-definition/dist/src/focused-plan'
import type { useGuidedModeStore } from '../stores/guidedMode'
import type {
  GuidedAnswer,
  GuidedAssessmentPath,
  GuidedModeDefinition,
  GuidedModeStep,
  GuidedModeSymptom,
} from '../types/guided-mode.types'
import { assessmentQuestionSteps } from '../utils/assessment-definition'
import {
  buildCalculatedDateStepDependencies,
  includeCalculatedDateDependents,
} from '../utils/calculated-date'
import { buildGuidedModeObservations } from '../utils/mrx-observations'
import { availableSymptoms, recordedEntry } from '../utils/symptoms'
import { useGuidedEntryPointRules } from './useGuidedEntryPointRules'

const mrxEngine = () => require('@/utils/mrxEngine') as typeof import('@/utils/mrxEngine')

export const FOCUSED_SYMPTOM_STEP_PREFIX = 'focused.symptom.'
const VITAL_SIGNS_TITLE_TOKEN = 'group.vital_signs.prompt'

interface StreamlinedOption {
  name: string
  preselect?: boolean
}

interface FocusedEvaluationResult {
  comprehensive?: boolean
  panels?: PlanPanel[]
}

interface FocusedEvaluationOptions {
  commitRules?: boolean
}

interface UseGuidedFocusedPlanOptions {
  store: ReturnType<typeof useGuidedModeStore>
  answers: Ref<Record<string, GuidedAnswer>>
  activeAssessmentPath: Ref<GuidedAssessmentPath | null>
  definition: ComputedRef<GuidedModeDefinition | null>
  excludedCodes: ComputedRef<string[]>
  escalated?: ComputedRef<boolean>
  reportError?: (error: unknown) => void
}

function leafSteps(definition: GuidedModeDefinition): GuidedModeStep[] {
  return (definition.assessment?.steps || []).flatMap(step => (step.kind === 'group' ? step.steps || [] : [step]))
}

export function useGuidedFocusedPlan({
  store, answers, activeAssessmentPath, definition, excludedCodes, escalated, reportError,
}: UseGuidedFocusedPlanOptions) {
  const evaluating = ref(false)
  let evaluationRun = 0
  let evaluatedKey: string | null = null
  const active = computed(() => store.consultationPath === 'focused' && Boolean(definition.value))
  const trackSymptoms = computed<GuidedModeSymptom[]>(() => (
    activeAssessmentPath.value ? store.clinicalContent.symptoms?.[activeAssessmentPath.value] || [] : []
  ))
  const planSymptoms = computed<GuidedModeSymptom[]>(() => availableSymptoms(trackSymptoms.value, answers.value)
    .map(symptom => {
      const entry = recordedEntry(symptom, answers.value) || symptom
      return { ...symptom, code: entry.code, conceptId: entry.conceptId, value: entry.value }
    }))
  const symptomById = computed(() => new Map(planSymptoms.value.map(symptom => [symptom.id, symptom])))
  const stepById = computed(() => new Map((definition.value ? leafSteps(definition.value) : [])
    .flatMap(step => (step.id ? [[step.id, step] as const] : []))))
  const groupByStepId = computed(() => new Map((definition.value?.assessment?.steps || [])
    .flatMap(group => (group.kind === 'group' ? group.steps || [] : [])
      .flatMap(step => (step.id ? [[step.id, group] as const] : [])))))
  const calculatedDateStepDependencies = computed(() => (
    buildCalculatedDateStepDependencies(definition.value)
  ))
  const planLeafEntries = computed(() => (definition.value
    ? planLeaves(definition.value as PlanDefinition)
    : []))
  const leafIdByConceptId = computed(() => new Map(planLeafEntries.value.flatMap(leaf => {
    const step = stepById.value.get(leaf.id)
    const conceptId = step?.questionId ? definition.value?.questions[step.questionId]?.conceptId : undefined
    return conceptId ? [[conceptId, leaf.id] as const] : []
  })))
  const entryPointRules = useGuidedEntryPointRules({
    store,
    answers,
    activeAssessmentPath,
    definition,
    excludedCodes,
    symptomById,
    stepById,
    planLeafEntries,
    leafIdByConceptId,
  })
  const {
    values,
    recordedEntryPointIds,
    activeAssumeIds,
    activeAskIds,
    askStepIds,
    selectedSymptomIds,
    triggerExclusions,
  } = entryPointRules

  const plan = computed<FocusedPlan | null>(() => {
    if (!active.value || !definition.value || !activeAssessmentPath.value) return null
    return buildFocusedPlan({
      definition: definition.value as PlanDefinition,
      symptoms: planSymptoms.value,
      selectedSymptomIds: selectedSymptomIds.value,
      answeredSymptomIds: recordedEntryPointIds.value,
      vitalSignRules: [],
      triggerExclusions: triggerExclusions.value,
      values: values.value,
      focusedStepIds: escalated?.value
        ? leafSteps(definition.value).flatMap(step => step.id || [])
        : [...(store.focusedStepIds || []), ...store.keptEscalationStepIds],
      excludedCodes: excludedCodes.value,
    })
  })

  const steps = computed<GuidedModeStep[] | null>(() => {
    if (!plan.value || !definition.value) return null
    const leaves = (ids: string[]) => ids.flatMap(id => stepById.value.get(id) || [])
    const withCalculatedDateOutputs = (stepIds: string[]) => (
      includeCalculatedDateDependents(stepIds, calculatedDateStepDependencies.value)
    )
    const leafAnswered = (stepId: string) => {
      const step = stepById.value.get(stepId)
      const conceptId = step?.questionId ? definition.value!.questions[step.questionId]?.conceptId : undefined
      const answer = conceptId ? answers.value[conceptId] : undefined
      return Array.isArray(answer) ? answer.length > 0 : answer !== undefined && answer !== null && answer !== ''
    }
    const triggered = new Set([...activeAssumeIds.value, ...activeAskIds.value])
    const mustConfirm = new Set(askStepIds.value)
    const introduced = new Set(store.introducedFocusedStepIds)
    const answeredWaveStepIds = new Set<string>()
    const stepsToAsk = (stepIds: string[], dynamic: boolean) => stepIds.filter(stepId => {
      if (!dynamic || !leafAnswered(stepId) || mustConfirm.has(stepId) || introduced.has(stepId)) return true
      answeredWaveStepIds.add(stepId)
      return false
    })
    const withGroupGate = (step: GuidedModeStep): GuidedModeStep => {
      const gate = groupByStepId.value.get(step.id!)?.visibleWhen
      if (!gate) return step
      return { ...step, visibleWhen: step.visibleWhen ? { all: [gate, step.visibleWhen] } : gate }
    }
    const symptomPages = (symptom: GuidedModeSymptom, stepIds: string[]): GuidedModeStep[] => {
      type Page = { checklist?: GuidedModeStep; groups: Set<GuidedModeStep | undefined>; stepIds: string[]; title: string }
      const pages: Page[] = []
      for (const stepId of stepIds) {
        const group = groupByStepId.value.get(stepId)
        const checklist = group?.multiselect ? group : undefined
        const title = group?.titleToken || symptom.labelToken
        const last = pages[pages.length - 1]
        if (last && last.checklist === checklist && last.title === title) {
          last.groups.add(group)
          last.stepIds.push(stepId)
        } else {
          pages.push({ checklist, title, groups: new Set([group]), stepIds: [stepId] })
        }
      }
      return pages.map((page, index) => {
        const source = page.groups.size === 1 ? Array.from(page.groups)[0] : undefined
        return {
          kind: 'group',
          id: `${FOCUSED_SYMPTOM_STEP_PREFIX}${symptom.id}${index ? `.${index}` : ''}`,
          titleToken: page.title,
          multiselect: Boolean(source?.multiselect),
          ...(source?.instructionsToken ? { instructionsToken: source.instructionsToken } : {}),
          ...(source?.media ? { media: source.media } : {}),
          ...(source?.noneOption ? { noneOption: source.noneOption } : {}),
          // Without a recorded presence answer, follow the questions' own gates.
          ...(triggered.has(symptom.id) || symptom.selectionOnly
            ? { steps: leaves(page.stepIds).map(withGroupGate) }
            : {
              visibleWhen: { all: [{ conceptId: symptom.conceptId, operator: 'eq', value: symptom.value }] },
              steps: leaves(page.stepIds).map(withGroupGate),
            }),
        }
      })
    }
    const result: GuidedModeStep[] = []
    const renderedLeafIds = new Set<string>()
    const answered = new Set(recordedEntryPointIds.value)
    const pagesAfterQuestion: Array<{ questionStepId?: string; pages: GuidedModeStep[] }> = []
    plan.value.waves.forEach((wave, index) => {
      for (const page of wave.pages) {
        const symptom = symptomById.value.get(page.symptomId)
        const stepIds = withCalculatedDateOutputs(
          stepsToAsk(page.stepIds, triggered.has(page.symptomId)),
        )
        if (symptom && stepIds.length) {
          const pages = symptomPages(symptom, stepIds)
          if (answered.has(symptom.id)) {
            pagesAfterQuestion.push({ questionStepId: leafIdByConceptId.value.get(symptom.conceptId), pages })
          } else result.push(...pages)
          stepIds.forEach(id => renderedLeafIds.add(id))
        }
      }
      const vitalSignStepIds = withCalculatedDateOutputs(
        stepsToAsk(
          wave.vitalSignStepIds,
          wave.symptomIds.length > 0 && wave.symptomIds.every(symptomId => triggered.has(symptomId)),
        ),
      )
      if (vitalSignStepIds.length) {
        result.push({
          kind: 'group',
          id: `focused.vital_signs.${index}`,
          titleToken: VITAL_SIGNS_TITLE_TOKEN,
          multiselect: false,
          steps: leaves(vitalSignStepIds),
        })
        vitalSignStepIds.forEach(id => renderedLeafIds.add(id))
      }
    })
    const retained = store.introducedFocusedStepIds.filter(leafAnswered)
    // Answered steps are never routed to, so a rule-recorded answer reaches the summary unasked.
    const recordedByRule = Object.entries(store.assumedSymptomAnswers)
      .flatMap(([symptomId, assumption]) => {
        const conceptId = assumption.conceptId || symptomById.value.get(symptomId)?.conceptId
        const stepId = conceptId ? leafIdByConceptId.value.get(conceptId) : undefined
        return stepId && leafAnswered(stepId) ? [stepId] : []
      })
    const forced = new Set(withCalculatedDateOutputs([
      ...mustConfirm, ...retained, ...answeredWaveStepIds, ...recordedByRule,
    ]))
    const assessmentSteps = assessmentQuestionSteps(definition.value, excludedCodes.value)
    for (const step of assessmentSteps) {
      if (step.kind === 'group') {
        const nested = (step.steps || [])
          .filter(nestedStep => nestedStep.id && forced.has(nestedStep.id) && !renderedLeafIds.has(nestedStep.id))
          .map(nestedStep => ({ ...nestedStep, visibleWhen: undefined }))
        if (nested.length) {
          result.push({
            ...step,
            id: `${step.id || 'group'}.focused`,
            visibleWhen: undefined,
            steps: nested,
          })
        }
      } else if (step.id && forced.has(step.id) && !renderedLeafIds.has(step.id)) {
        result.push({ ...step, visibleWhen: undefined })
      }
    }
    const remaining = new Set(withCalculatedDateOutputs(plan.value.remainingStepIds))
    for (const step of assessmentSteps) {
      if (step.kind === 'group') {
        const nested = (step.steps || [])
          .filter(nestedStep => nestedStep.id && remaining.has(nestedStep.id)
            && !forced.has(nestedStep.id) && !renderedLeafIds.has(nestedStep.id))
        if (nested.length) {
          result.push({ ...step, steps: nested })
        }
      } else if (step.id && remaining.has(step.id) && !forced.has(step.id) && !renderedLeafIds.has(step.id)) {
        result.push(step)
      }
    }
    for (const { questionStepId, pages } of pagesAfterQuestion) {
      const index = result.findIndex(step => (step.kind === 'group' ? step.steps || [] : [step])
        .some(leaf => leaf.id === questionStepId))
      result.splice(index < 0 ? result.length : index + 1, 0, ...pages)
    }
    return result
  })

  const startedConceptIds = computed(() => (plan.value?.startedSymptomIds || [])
    .flatMap(id => {
      const symptom = symptomById.value.get(id)
      return symptom && !symptom.selectionOnly ? [symptom.conceptId] : []
    }))
  const startedFocusedOptions = computed(() => Array.from(new Set([
    ...selectedSymptomIds.value,
    ...(plan.value?.startedSymptomIds || []),
  ]
    .flatMap(id => symptomById.value.get(id)?.focusedOptions || []))))

  async function selectedOptions(path: GuidedAssessmentPath, currentDefinition: GuidedModeDefinition): Promise<string[]> {
    const responses = buildGuidedModeObservations(currentDefinition, answers.value)
      .map(observation => ({ type: 'response', name: observation.name, value: String(observation.value) }))
    const converted = await mrxEngine().convertFlexi(responses)
    const classifications = [
      { type: 'classification', name: 'patient', value: path },
      { type: 'classification', name: 'audience', value: 'chw' },
      { type: 'classification', name: 'experience', value: 'pwa' },
    ]
    const options = await mrxEngine().streamlinedOptions([...classifications, ...(Array.isArray(converted) ? converted : [])])
    if (!Array.isArray(options)) throw new Error('Unable to load streamlined options')
    const symptomOptions = new Set(startedFocusedOptions.value)
    const available: StreamlinedOption[] = options.length
      ? options
      : Array.from(symptomOptions, name => ({ name }))
    return available
      .filter(option => option.preselect || symptomOptions.has(option.name))
      .map(option => option.name)
  }

  // Resolves true when the focused steps come from MRX, false when they fell back to every step.
  async function evaluate({ commitRules = true }: FocusedEvaluationOptions = {}): Promise<boolean> {
    const currentDefinition = definition.value
    const path = activeAssessmentPath.value
    if (!active.value || !currentDefinition || !path) return false
    if (commitRules) entryPointRules.commit()
    const sortedAnswers = Object.entries(answers.value).sort(([left], [right]) => left.localeCompare(right))
    const key = JSON.stringify([
      path,
      sortedAnswers,
      selectedSymptomIds.value,
      store.committedAssumeSymptomIds,
      store.committedAskSymptomIds,
      store.confirmedAskSymptomIds,
    ])
    if (key === evaluatedKey && store.focusedStepIds) return true
    const run = ++evaluationRun
    const allStepIds = leafSteps(currentDefinition).flatMap(step => step.id || [])
    const previousFocusedStepIds = store.focusedStepIds
    evaluating.value = true
    try {
      const selections = await selectedOptions(path, currentDefinition)
      const result = await mrxEngine().focusedEvaluationFlexi({
        classifications: { patient: path, audience: 'chw', experience: 'pwa' },
        streamlinedSelections: selections,
      }) as FocusedEvaluationResult | null
      if (!result?.comprehensive && !Array.isArray(result?.panels)) {
        throw new Error('Unable to evaluate the focused assessment')
      }
      if (run !== evaluationRun) return false
      const nextFocusedStepIds = result.comprehensive
        ? allStepIds
        : selectFocusedSteps(currentDefinition as PlanDefinition, result.panels!).stepIds
      store.focusedStepIds = nextFocusedStepIds
      // Escalation adds every question; none of them was introduced by a started symptom.
      entryPointRules.recordIntroducedSteps(
        plan.value,
        result.comprehensive ? null : previousFocusedStepIds,
        nextFocusedStepIds,
      )
      evaluatedKey = key
      return true
    } catch (error) {
      if (run !== evaluationRun) return false
      reportError?.(error)
      evaluatedKey = null
      store.focusedStepIds = allStepIds
      entryPointRules.recordIntroducedSteps(
        plan.value,
        escalated?.value ? null : previousFocusedStepIds,
        allStepIds,
      )
      return false
    } finally {
      if (run === evaluationRun) evaluating.value = false
    }
  }

  function cancelEvaluation() {
    evaluationRun += 1
    evaluating.value = false
  }

  // Symptoms evaluates on Next itself, after the escalation check.
  watch(active, isActive => {
    if (isActive && store.focusedStepIds === null && !evaluating.value && store.view !== 'symptoms') void evaluate()
  }, { immediate: true })

  function reset() {
    cancelEvaluation()
    evaluatedKey = null
    store.focusedStepIds = null
    entryPointRules.reset()
  }

  return {
    active, evaluating, plan, steps, startedConceptIds, startedFocusedOptions, evaluate,
    commitEntryPointRules: entryPointRules.commit, cancelEvaluation, reset,
  }
}
